import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useLoading } from "tap/utils/hooks/useLoading.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
import { useJobsActions } from "tap/store/licensingJobs/useJobs.js";
import { tapApi } from "tap/api.js";
import { filterCriteria } from "tap/utils/filterCriteria.js";

// Minimum time to show the loading spinner (in ms)
const MIN_LOADING_DURATION = 400;

/**
 * Custom hook that consolidates Licensing Jobs data fetching logic.
 * Handles loading states, error toasts, and Redux updates.
 *
 * @returns {Object} Object containing fetchLicensingJobs function and state values
 */
export function useLicensingJobsData() {
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { showErrorToast } = useToasts();
  const { updateJobs, clearJobs } = useJobsActions();

  const {
    filterForm,
    licJobRange,
    licJobSort,
    itemsPerPage,
    defaultFilterRange,
  } = useSelector((s) => s.licensingJobs);

  /**
   * Fetches Licensing Jobs from the API with the given range.
   * Ensures loading spinner is visible for at least MIN_LOADING_DURATION.
   * @param {string} range - The pagination range (e.g., "0,25")
   * @param {Object|null} customFilterData - Optional filter data to use instead of Redux filterForm
   * @param {Array|null} customSort - Optional sort array to use instead of Redux licJobSort
   * @param {boolean} silent - If true, don't show loading indicator (for auto-refresh)
   * @returns {Promise<Object|null>} The API response data or null on error
   */
  const fetchLicensingJobs = useCallback(
    async (
      range,
      customFilterData = null,
      customSort = null,
      silent = false,
    ) => {
      const startTime = Date.now();
      if (!silent) {
        setLoadingTrue();
      }

      const filterData = customFilterData ?? filterForm;
      const sortData = customSort ?? licJobSort;

      try {
        const data = await tapApi.licensingJobs(
          filterCriteria(filterData, "", "licensingJobs"),
          range,
          sortData,
        );

        if (data?.code || data?.httpCode || data?.error) {
          if (!silent) {
            showErrorToast(
              data?.message || "Failed to fetch licensing jobs data.",
            );
          }
          return null;
        }

        return data;
      } catch (error) {
        if (!silent) {
          showErrorToast(
            error?.message || "Failed to fetch licensing jobs data.",
          );
        }
        console.error("Error fetching licensing jobs:", error);
        return null;
      } finally {
        if (!silent) {
          // Ensure minimum loading duration for consistent UX
          const elapsed = Date.now() - startTime;
          const remainingTime = MIN_LOADING_DURATION - elapsed;

          if (remainingTime > 0) {
            setTimeout(() => setLoadingFalse(), remainingTime);
          } else {
            setLoadingFalse();
          }
        }
      }
    },
    [filterForm, licJobSort, setLoadingTrue, setLoadingFalse, showErrorToast],
  );

  return {
    fetchLicensingJobs,
    updateJobs,
    clearJobs,
    itemsPerPage,
    licJobRange,
    licJobSort,
    filterForm,
    defaultFilterRange,
  };
}
